import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComment,
  faClock,
  faArrowLeft,
  faShare,
  faBookmark,
  faEye,
  faCalendar,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faTwitter,
  faLinkedin,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import apiService from "../../ApiService";
import { formatDateTime } from "../../utils/DateUtils";
import { baseUrl } from "../../config";
import hljs from "highlight.js";
import "highlight.js/styles/atom-one-dark.css";
import SubscribeModal from "../../components/GNSubscribeModal";
import { trackEvent } from "../../components/trackingUtils";
import SEO from "../../common/SEO";
import parse from "html-react-parser";
import Reveal from "../../components/Reveal";
import "./BlogsDetails.css";

const countWords = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent || div.innerText || "";
  return text.trim().split(/\s+/).length;
};

const calculateReadingTime = (wordCount) => {
  const wordsPerMinute = 200;
  return Math.ceil(wordCount / wordsPerMinute);
};

const BlogDetails = ({ SEOUrl, Category }) => {
  const history = useHistory();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const fetchReactions = useCallback(async (postId) => {
    try {
      const reactionCounts = await apiService.getBlogReactionByPostId(postId);
      const reactions = {
        like: 0,
        dislike: 0,
        heart: 0,
        clap: 0,
      };
      reactionCounts.data.forEach((reaction) => {
        reactions[reaction.reaction] = reaction.count;
      });
      return reactions;
    } catch (error) {
      console.error("Error fetching reactions:", error);
      return {
        like: 0,
        dislike: 0,
        heart: 0,
        clap: 0,
      };
    }
  }, []);

  const fetchBlogPost = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!Category || !SEOUrl) {
        console.error("Category and SEO URL parameters are required");
        setError("Invalid blog URL");
        setLoading(false);
        return;
      }
      // Try fetching by category + SEOUrl first
      let postData = await apiService.getBlogPost(Category, SEOUrl);

      // Fallback: if not found, attempt fetch by SEOUrl only
      if (!postData) {
        postData = await apiService.getBlogPostBySeoUrl(SEOUrl);
      }

      // Final Fallback: load all blogs and match locally
      if (!postData) {
        try {
          const all = await apiService.fetchBlog();
          const fromAll = all?.records?.find(
            (r) =>
              String(r.SEOUrl).toLowerCase() === String(SEOUrl).toLowerCase()
          );
          if (fromAll) {
            postData = fromAll;
          }
        } catch (e) {
          console.warn("Fallback fetchBlog failed:", e);
        }
      }

      if (!postData) {
        setError("Article not found");
        setPost(null);
        setLoading(false);
        return;
      }

      console.log("Post data loaded:", postData); // Debug log
      console.log("Post content preview:", postData.Content?.substring(0, 200)); // Debug log

      const wordCount = countWords(postData.Content);
      const reactions = await fetchReactions(postData.PostID);
      postData.BlogReadTime = `${calculateReadingTime(wordCount)} min read`;
      setPost({
        ...postData,
        reactions,
      });
    } catch (error) {
      console.error("Error fetching blog post:", error);
      setError(error.message || "Failed to load article");
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [Category, SEOUrl, fetchReactions]);

  useEffect(() => {
    trackEvent("PageLoad", "", "BlogDetails");
    fetchBlogPost();
  }, [fetchBlogPost]);

  useEffect(() => {
    // Apply syntax highlighting to the blog content
    if (post) {
      hljs.configure({
        languages: [
          "bash",
          "csharp",
          "cmd",
          "javascript",
          "php",
          "python",
          "json",
          "xml",
          "html",
          "css",
          "sql",
        ], // Extended language support
      });

      // Add a small delay to ensure DOM is updated
      setTimeout(() => {
        // Target various code block formats
        const codeBlocks = document.querySelectorAll(
          ".blog-content pre.ql-syntax, .blog-content pre code, .blog-content code.ql-syntax"
        );

        codeBlocks.forEach((block) => {
          // Skip if already highlighted
          if (!block.classList.contains("hljs")) {
            hljs.highlightElement(block);
          }
        });
      }, 100);
    }
  }, [post]);

  const shareUrl = baseUrl + `/blogs/${Category}/${post?.SEOUrl}`;
  const shareText = post?.Title;

  // Safely render post content: enhanced for all types of HTML content
  const renderPostContent = () => {
    if (!post || !post.Content) return null;

    // Clean and enhance HTML content for better rendering
    let content = post.Content;

    try {
      // More comprehensive HTML cleaning and preparation
      content = content
        // Handle various HTML entity encodings
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&nbsp;/g, " ")

        // Handle Quill editor specific markup
        .replace(/class="ql-syntax"/g, 'class="ql-syntax language-javascript"')
        .replace(/<pre([^>]*)spellcheck="false"([^>]*)>/g, "<pre$1$2>")

        // Ensure proper closing tags for self-closing elements
        .replace(/<br>/g, "<br />")
        .replace(/<hr>/g, "<hr />")

        // Handle malformed or nested tags that might cause parsing issues
        .replace(/<\/?\s*>/g, "") // Remove empty tags
        .replace(/(<\w+)([^>]*?)\/>/g, "$1$2></$1>"); // Convert self-closing to proper closing

      // Additional validation and cleaning
      if (content.trim() === "") {
        return <div className="no-content">No content available</div>;
      }

      // Try parsing with html-react-parser
      const parsed = parse(content, {
        replace: (domNode) => {
          // Handle specific problematic elements
          if (domNode.name === "script") {
            return <div>Script content removed for security</div>;
          }
        },
      });

      return parsed;
    } catch (e) {
      console.error(
        "Error parsing post content, falling back to innerHTML:",
        e
      );
      console.log("Original content:", post.Content);
      console.log("Processed content:", content);

      // Final fallback with safer HTML injection
      return (
        <div
          className="fallback-html"
          dangerouslySetInnerHTML={{
            __html: content.replace(
              /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
              ""
            ), // Remove any script tags
          }}
        />
      );
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-[#0b1220]"></div>
          <p className="mt-4 text-gray-600 text-lg">Loading article...</p>
        </div>
      </div>
    );
  }

  // Error state - Show 404 page
  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white px-4">
        <div className="max-w-2xl w-full text-center">
          <div className="mb-8">
            <div className="text-9xl font-bold text-gray-200">404</div>
            <h1 className="text-4xl font-bold text-gray-800 mt-4 mb-2">
              Article Not Found
            </h1>
            <p className="text-gray-600 text-lg mb-8">
              {error ||
                "The article you're looking for doesn't exist or has been removed."}
            </p>
          </div>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => history.push("/blogs")}
              className="px-6 py-3 bg-[#0b1220] text-white rounded-lg hover:bg-[#1a2332] transition-colors"
            >
              Browse All Articles
            </button>
            <button
              onClick={() => history.push("/")}
              className="px-6 py-3 border-2 border-[#0b1220] text-[#0b1220] rounded-lg hover:bg-gray-50 transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {post && (
        <>
          {/* JSON-LD Structured Data for Article */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: post.Title,
              description: post.MetaDescription,
              image: baseUrl + post.ImageUrl,
              datePublished: post.CreatedDate,
              dateModified: post.ModifiedDate,
              author: {
                "@type": "Person",
                name: post.Author || "GeniusNexa Team",
              },
              publisher: {
                "@type": "Organization",
                name: "GeniusNexa",
                url: baseUrl,
                logo: {
                  "@type": "ImageObject",
                  url: `${baseUrl}/images/logo.png`,
                },
              },
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": shareUrl,
              },
              articleBody: post.Content
                ? post.Content.replace(/<[^>]*>/g, "").substring(0, 500)
                : "",
              keywords: post.MetaKeywords,
              articleSection: post.Category,
              wordCount: countWords(post.Content),
              timeRequired: post.BlogReadTime,
              inLanguage: "en-US",
            })}
          </script>

          {/* JSON-LD Breadcrumb */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: baseUrl,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Blog",
                  item: `${baseUrl}/blogs`,
                },
                ...(Category
                  ? [
                      {
                        "@type": "ListItem",
                        position: 3,
                        name: Category,
                        item: `${baseUrl}/blogs/${Category}`,
                      },
                    ]
                  : []),
                {
                  "@type": "ListItem",
                  position: Category ? 4 : 3,
                  name: post.Title,
                  item: shareUrl,
                },
              ],
            })}
          </script>

          <SEO
            title={`${shareText} | GeniusNexa Blog 2025`}
            description={post.MetaDescription}
            keywords={post.MetaKeywords}
            image={baseUrl + post.ImageUrl}
            url={shareUrl}
            type="article"
            facebook={
              post.FacebookImageUrl ? baseUrl + post.FacebookImageUrl : null
            }
            twitter={
              post.TwitterImageUrl ? baseUrl + post.TwitterImageUrl : null
            }
            whatsapp={
              post.WhatsappImageUrl ? baseUrl + post.WhatsappImageUrl : null
            }
            linkedin={
              post.LinkedinImageUrl ? baseUrl + post.LinkedinImageUrl : null
            }
            teams={post.TeamsImageUrl ? baseUrl + post.TeamsImageUrl : null}
            appletouchicon={
              post.AppleTouchIcon
                ? post.AppleTouchIcon.split(",")
                    .map((icon) => baseUrl + icon.trim())
                    .join(",")
                : null
            }
            windowsicon={
              post.WindowsIcon
                ? post.WindowsIcon.split(",")
                    .map((icon) => baseUrl + icon.trim())
                    .join(",")
                : null
            }
            androidicons={
              post.AndroidIcon
                ? post.AndroidIcon.split(",")
                    .map((icon) => baseUrl + icon.trim())
                    .join(",")
                : null
            }
          />
        </>
      )}
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto pt-4 pb-8 px-4 max-w-7xl">
          {loading && (
            <div className="text-center py-20">
              <div className="w-16 h-16 border-4 border-[#E5C558] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Loading article...
              </p>
            </div>
          )}
          {post && (
            <>
              {/* Breadcrumb Navigation */}
              <Reveal>
                <button
                  onClick={() =>
                    Category
                      ? history.push(`/blogs/${Category}`)
                      : history.push("/blogs")
                  }
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-[#E5C558] dark:hover:text-[#E5C558] transition-colors mb-4 group"
                >
                  <FontAwesomeIcon
                    icon={faArrowLeft}
                    className="group-hover:-translate-x-1 transition-transform"
                  />
                  <span>Back to {Category ? Category : "All Articles"}</span>
                </button>
              </Reveal>

              <article className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden">
                {/* Header Image */}
                <Reveal>
                  <div className="relative h-96 overflow-hidden group">
                    <img
                      src={baseUrl + post.ImageUrl}
                      alt={post.Title}
                      className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                      onClick={() => setShowImageModal(true)}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                    {/* View Full Image Icon */}
                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex items-center gap-2 px-3 py-2 bg-black/50 text-white rounded-full backdrop-blur-sm">
                        <FontAwesomeIcon icon={faEye} className="text-sm" />
                        <span className="text-sm font-medium">
                          View Full Image
                        </span>
                      </div>
                    </div>

                    {/* Category Badge */}
                    {post.Category && (
                      <div className="absolute top-6 left-6 px-4 py-2 bg-[#E5C558] text-gray-900 font-bold rounded-full shadow-lg">
                        {post.Category}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="absolute top-6 right-6 flex gap-3">
                      <button
                        onClick={() => setIsBookmarked(!isBookmarked)}
                        className={`w-12 h-12 rounded-full backdrop-blur-md border shadow-lg ${
                          isBookmarked
                            ? "bg-[#E5C558] border-[#E5C558] text-gray-900"
                            : "bg-black/30 border-white/30 text-white hover:bg-black/50 hover:border-white/50"
                        } transition-all flex items-center justify-center`}
                      >
                        <FontAwesomeIcon
                          icon={faBookmark}
                          className="text-lg"
                        />
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setShowShareMenu(!showShareMenu)}
                          className="w-12 h-12 rounded-full bg-black/30 backdrop-blur-md border border-white/30 text-white hover:bg-black/50 hover:border-white/50 transition-all flex items-center justify-center shadow-lg"
                        >
                          <FontAwesomeIcon icon={faShare} className="text-lg" />
                        </button>

                        {/* Share Menu */}
                        {showShareMenu && (
                          <div className="absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-4 w-64 border border-gray-200 dark:border-gray-700">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                              Share this article
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <a
                                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                              >
                                <FontAwesomeIcon icon={faFacebook} />
                                Facebook
                              </a>
                              <a
                                href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm"
                              >
                                <FontAwesomeIcon icon={faTwitter} />
                                Twitter
                              </a>
                              <a
                                href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${shareText}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors text-sm"
                              >
                                <FontAwesomeIcon icon={faLinkedin} />
                                LinkedIn
                              </a>
                              <a
                                href={`https://wa.me/?text=${encodeURIComponent(
                                  shareText + " " + shareUrl
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                              >
                                <FontAwesomeIcon icon={faWhatsapp} />
                                WhatsApp
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>

                {/* Content */}
                <div className="p-8 md:p-12">
                  {/* Article Header */}
                  <header>
                    {/* Title */}
                    <Reveal>
                      <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">
                        {post.Title}
                      </h1>
                    </Reveal>

                    {/* Meta Info */}
                    <Reveal>
                      <div className="flex flex-wrap items-center gap-6 pb-8 mb-8 border-b border-gray-200 dark:border-gray-700">
                        {/* Author */}
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E5C558] to-[#d4b54d] flex items-center justify-center text-gray-900 font-bold text-lg">
                            {post.Author?.charAt(0).toUpperCase() || "A"}
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <FontAwesomeIcon
                                icon={faUser}
                                className="text-xs"
                              />
                              Author
                            </p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {post.Author || "GeniusNexa Team"}
                            </p>
                          </div>
                        </div>

                        {/* Published Date */}
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <FontAwesomeIcon
                              icon={faCalendar}
                              className="text-xs"
                            />
                            Published
                          </p>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {formatDateTime(post.ModifiedDate)}
                          </p>
                        </div>

                        {/* Reading Time */}
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <FontAwesomeIcon
                              icon={faClock}
                              className="text-xs"
                            />
                            Reading Time
                          </p>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {post.BlogReadTime}
                          </p>
                        </div>

                        {/* Views (if available) */}
                        {post.Views && (
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <FontAwesomeIcon
                                icon={faEye}
                                className="text-xs"
                              />
                              Views
                            </p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {post.Views.toLocaleString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </Reveal>
                  </header>

                  {/* Photo Credit */}
                  {post.PhotoCredit && (
                    <Reveal>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-8 italic">
                        {post.PhotoCredit}
                      </div>
                    </Reveal>
                  )}

                  {/* Article Content - Clean Display */}
                  <div className="blog-content-clean">
                    {post?.Content ? (
                      renderPostContent()
                    ) : (
                      <div className="text-center py-8 bg-gray-100 rounded-lg">
                        <p className="text-gray-600 text-lg">
                          Content is loading or temporarily unavailable.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Comments Count & Share Footer */}
                  <Reveal>
                    <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                        {/* Comments */}
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <FontAwesomeIcon
                            icon={faComment}
                            className="text-[#E5C558]"
                          />
                          <span className="font-medium">
                            {post.Comments?.length || 0}{" "}
                            {post.Comments?.length === 1
                              ? "Comment"
                              : "Comments"}
                          </span>
                        </div>

                        {/* Share CTA */}
                        <div className="flex items-center gap-4">
                          <span className="text-gray-600 dark:text-gray-400 font-medium">
                            Share:
                          </span>
                          <div className="flex gap-3">
                            <a
                              href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:scale-110 transition-transform"
                              title="Share on Facebook"
                            >
                              <FontAwesomeIcon icon={faFacebook} />
                            </a>
                            <a
                              href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-10 h-10 rounded-full bg-sky-500 text-white flex items-center justify-center hover:scale-110 transition-transform"
                              title="Share on Twitter"
                            >
                              <FontAwesomeIcon icon={faTwitter} />
                            </a>
                            <a
                              href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${shareText}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-10 h-10 rounded-full bg-blue-700 text-white flex items-center justify-center hover:scale-110 transition-transform"
                              title="Share on LinkedIn"
                            >
                              <FontAwesomeIcon icon={faLinkedin} />
                            </a>
                            <a
                              href={`https://wa.me/?text=${encodeURIComponent(
                                shareText + " " + shareUrl
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center hover:scale-110 transition-transform"
                              title="Share on WhatsApp"
                            >
                              <FontAwesomeIcon icon={faWhatsapp} />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                </div>
              </article>

              {/* Back to Blog Button */}
              <Reveal>
                <div className="text-center mt-12">
                  <button
                    onClick={() => history.push("/blogs")}
                    className="px-8 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-gray-900 font-bold rounded-xl hover:shadow-xl hover:shadow-[#E5C558]/30 hover:scale-105 transition-all duration-300"
                  >
                    Explore More Articles
                  </button>
                </div>
              </Reveal>
            </>
          )}
          {!loading && !post && (
            <Reveal>
              <div className="text-center py-20">
                <div className="max-w-md mx-auto">
                  <svg
                    className="w-24 h-24 mx-auto text-gray-300 dark:text-gray-600 mb-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="text-3xl font-bold text-gray-700 dark:text-gray-300 mb-3">
                    Article Not Found
                  </h3>
                  <p className="text-lg text-gray-500 dark:text-gray-400 mb-6">
                    The article you're looking for doesn't exist or has been
                    removed.
                  </p>
                  <button
                    onClick={() => history.push("/blogs")}
                    className="px-6 py-3 bg-[#E5C558] text-gray-900 font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Back to Blog
                  </button>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </div>
      <SubscribeModal show={showModal} handleClose={handleCloseModal} />

      {/* Full Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm">
          {/* Close Button */}
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-6 right-6 z-20 w-14 h-14 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all border border-white/20 hover:border-white/40"
          >
            <svg
              className="w-7 h-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Image Container */}
          <div className="w-full h-full flex items-center justify-center p-6">
            <div className="relative max-w-full max-h-full">
              {/* Full Image */}
              <img
                src={baseUrl + post.ImageUrl}
                alt={post.Title}
                className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl"
                style={{ maxWidth: "95vw", maxHeight: "90vh" }}
              />

              {/* Image Caption */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4 rounded-b-lg">
                <h3 className="text-white text-lg font-semibold mb-1">
                  {post.Title}
                </h3>
                <p className="text-gray-300 text-sm">
                  Click anywhere outside to close
                </p>
              </div>
            </div>
          </div>

          {/* Click Outside to Close */}
          <div
            className="absolute inset-0 -z-10"
            onClick={() => setShowImageModal(false)}
          ></div>
        </div>
      )}
    </>
  );
};

export default BlogDetails;
